import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { IPlaybackScene } from '../../../video-trim/video-trimmer/video-trimmer-model';
import { MediaDisplayerSettings } from './media-displayer-model';
import { IStreamUrls } from '../../../../../../../directives/video-source.directive';
import { Subject } from 'rxjs';
import { VideoLayerUtils } from '../../../../../../../models/project/take/layers/video-layer-utils';
import { AspectRatioType } from '../../../../../../../directives/aspect-ratio.directive';

@Component({
    selector: 'app-media-displayer',
    templateUrl: './media-displayer.component.html',
    styleUrl: './media-displayer.component.scss',
})
export class MediaDisplayerComponent implements IPlaybackScene, OnDestroy, AfterViewInit {
    ngAfterViewInit(): void {
        this.applyMediaStreamIfNeeded();
    }
    settings: MediaDisplayerSettings;
    @Input({ alias: 'display-configs', required: true })
    previewSettings: {
        isVideo: boolean;
        streamUrls: IStreamUrls;
        toFlipHorizontal: boolean;
        toDisplayControls: boolean;
        toMute: boolean;
    };
    @ViewChild('mediaRef', { static: false })
    mediaRef!: ElementRef<HTMLMediaElement>;

    @Input({ alias: 'playOrPause' })
    set setPlayOrPause(toPlay: boolean) {
        const mediaElement = this.mediaElement;
        if (!mediaElement) {
            return;
        }
        if (toPlay) {
            mediaElement.play();
        } else {
            mediaElement.pause();
        }
    }
    onDestroy$ = new Subject<boolean>();

    @Output('trimReached') trimReached = new EventEmitter<void>();
    @Output('onCanPlayThrough') onCanPlayThrough = new EventEmitter<{ id: string }>();

    mediaElement: HTMLMediaElement;
    trimEnd: number;
    trimStart: number;
    trimStartAsSeconds: number;
    trimEndAsSeconds: number;
    duration = 0;
    originalDuration: number = 0;
    currentTime = 0;
    isPlaying = false;
    private readonly ASPECT_TOLERANCE = 0.03; // ~3%
    aspectRatioType: AspectRatioType = 'unknown';
    isHorizontalControls = true;

    @Input({ alias: 'media-displayer-settings', required: true })
    set mediaDisplayerSettings(settings: MediaDisplayerSettings) {
        this.settings = settings;
        this.applyMediaStreamIfNeeded();
        this.duration = settings?.duration;
        this.originalDuration = this.duration;
        this.applyTrims();
    }
    async onTimeUpdate() {
        if (!this.mediaElement) return;

        const media = this.mediaElement;

        // 1️⃣ Absolute time from start of original video
        const absoluteMs = media.currentTime * 1000;

        // 2️⃣ Real trimmed region in absolute coordinates
        const absoluteTrimStart = this.trimStart; // ms
        const absoluteTrimEnd = this.originalDuration - this.trimEnd; // ms

        // 3️⃣ Relative time for scrub bar (0 at trimStart)
        const clampedAbsolute = Math.min(Math.max(absoluteMs, absoluteTrimStart), absoluteTrimEnd);
        const relativeMs = clampedAbsolute - absoluteTrimStart;

        this.currentTime = relativeMs;
        if (relativeMs < 0) {
            console.log(`NEW TIMR ERRRORRRRR`, this.currentTime);
        }

        // 4️⃣ Stop when we actually reach the end of the trimmed region
        const playbackStrategy = this.settings.playbackStrategy;
        if (playbackStrategy) {
            await VideoLayerUtils.playbackMediaByPlaybackStrategyAsync(
                this.trimStartAsSeconds,
                this.trimEndAsSeconds,
                this.mediaElement,
                playbackStrategy,
                this.settings.uniqueId
            );
        } else {
            if (absoluteMs >= absoluteTrimEnd) {
                media.pause();
                media.currentTime = this.trimStartAsSeconds;
                this.isPlaying = false;
                this.trimReached.emit();
            }
        }
    }

    emitNewTime(timeInMilliSeconds: number): void {
        if (!this.mediaElement) return;

        const relativeSeconds = timeInMilliSeconds / 1000;

        // REAL trim start
        const absoluteTrimStart = this.trimStartAsSeconds;

        // REAL trim end (since trimEnd=seconds trimmed from end)
        const absoluteTrimEnd = this.originalDuration - this.trimEndAsSeconds;

        // Convert relative → absolute
        let absoluteSeconds = absoluteTrimStart + relativeSeconds;

        // Clamp to trim boundaries
        if (absoluteSeconds < absoluteTrimStart) {
            absoluteSeconds = absoluteTrimStart;
        }

        if (absoluteSeconds > absoluteTrimEnd) {
            absoluteSeconds = absoluteTrimEnd;
        }

        this.mediaElement.currentTime = absoluteSeconds;
    }

    onVideoEnded() {
        const media = this.mediaElement;
        if (!media) return;
        media.currentTime = this.trimStartAsSeconds;
        this.isPlaying = false;
    }

    togglePlayMedia() {
        const media = this.mediaElement;

        if (media.paused) {
            // If at or past trim end, reset to trim start before playing
            if (media.currentTime >= (this.originalDuration - this.trimEnd) / 1000 - 0.05) {
                media.currentTime = this.trimStartAsSeconds;
            }
            media.play();
            this.isPlaying = true;
        } else {
            media.pause();
            this.isPlaying = false;
        }
    }

    updateTrimEnd(trimEnd: number) {
        this.settings.trimEnd = trimEnd;
        console.log(`New TRIM END`, trimEnd);
        this.applyTrims();
    }

    updateTrimStart(trimStart: number) {
        this.settings.trimStart = trimStart;
        console.log(`nEW TRIM start`, trimStart);

        this.applyTrims();
    }

    onLoadedMetadata(event: Event, bla: string) {
        console.log(`IMMMM`, bla);
        const media = event.target as HTMLMediaElement;
        this.mediaElement = media;

        // IMPORTANT: remove old listener if exists
        media.removeEventListener('canplay', this.canPlayHandler);

        // Add a SINGLE listener
        media.addEventListener('canplay', this.canPlayHandler);
        this.applyTrims();
    }

    validateTrims() {
        if (this.trimStart < 0) this.trimStart = 0;
        if (this.trimEnd < 0) this.trimEnd = 0;

        // Fix impossible trim: trimmed more than video length
        if (this.trimStart + this.trimEnd >= this.originalDuration) {
            // Clamp trimEnd so a 1ms region remains
            this.trimEnd = this.originalDuration - this.trimStart - 1;
        }
    }

    setScrubTimeVisibilty(isVisible: boolean): void {}

    private applyTrims() {
        const media = this.mediaElement;
        if (!media) return;

        this.trimStart = this.settings.trimStart;
        this.trimEnd = this.settings.trimEnd;
        this.validateTrims();
        console.log(`THIS IS TRIM END!`, this.trimEnd);
        this.trimStartAsSeconds = this.trimStart / 1000;
        this.trimEndAsSeconds = this.trimEnd / 1000;
        this.duration = this.originalDuration - this.trimEnd - this.trimStart;

        media.currentTime = this.trimStartAsSeconds;

        if (media.currentTime >= this.trimEndAsSeconds) {
            media.pause();
            media.currentTime = this.trimStartAsSeconds;
        }
    }
    private canPlayHandler = () => {
        this.onCanPlayThrough.emit({ id: this.settings.uniqueId });
        console.log('CAN PLAY ONCE!', this.settings.uniqueId);

        // Remove the listener immediately to prevent spam
        if (this.mediaElement) {
            this.mediaElement.removeEventListener('canplay', this.canPlayHandler);
        }
    };

    private applyMediaStreamIfNeeded() {
        if (!this.mediaRef || !this.settings) return;
        if (this.settings.type === 'live' && this.settings.mediaStream) {
            const videoElement = this.mediaRef.nativeElement;

            if (videoElement.srcObject !== this.settings.mediaStream) {
                videoElement.srcObject = this.settings.mediaStream;
            }
        }
    }
    getAspectRatioType(width: number, height: number): AspectRatioType {
        if (!width || !height) return 'unknown';

        const ratio = width / height;

        if (Math.abs(ratio - 16 / 9) < this.ASPECT_TOLERANCE) {
            return '16:9';
        }

        if (Math.abs(ratio - 1) < this.ASPECT_TOLERANCE) {
            return '1:1';
        }

        if (Math.abs(ratio - 9 / 16) < this.ASPECT_TOLERANCE) {
            return '9:16';
        }

        return 'unknown';
    }
    onAspectRatioChange(type: AspectRatioType): void {
        this.aspectRatioType = type;
        this.isHorizontalControls = type === '16:9' || type === 'unknown';
    }
    ngOnDestroy() {
        console.log('media displayer destroyed');

        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }
}
